import { usePlayerActions } from "@utils/player/core/usePlayer";
import LBox from "@components/LBox";
import { useStableWindowDimensions } from "@hooks/useStableWindowDimensions";
import { useNavigationStore } from "@utils/navigation/useNavigationStore";
import AppModalSheet from "@utils/appModal/components/AppModalSheet";
import { getViewPort } from "@utils/web/config";
import { useAuth } from "@utils/firebaseAuth/auth";
import Auth from "app/login";
import { forwardRef, useCallback, useEffect, useMemo, useRef } from "react";
import { ActivityIndicator, StyleSheet, View } from "react-native";
import { type SheetAdapterRef } from "react-native-bottom-sheet-stack";
import { APP_MODAL_CORNER_RADIUS } from "@utils/appModal/constants";
import {
  type CollectionType,
  useActiveCollectionId,
  useActiveCollectionType,
  useActiveSongId,
} from "@utils/player";

const DarkSheetSurface = () => (
  <View style={[StyleSheet.absoluteFill, styles.darkSurface]} />
);

const AuthModalContent = ({ closeModal }: { closeModal: () => void }) => {
  const playerActions = usePlayerActions();

  const screen = useStableWindowDimensions();
  const { width, height } = getViewPort(screen);
  const availableHeight = height * 0.8;

  const { status } = useAuth();
  const playFromCollection = playerActions.playFromCollection;
  const activeSongId = useActiveSongId();
  const activeCollectionType = useActiveCollectionType();
  const activeCollectionId = useActiveCollectionId();

  const isLoading = useMemo(() => {
    return status !== "no-user" && status !== "logged-in";
  }, [status]);

  const beforeAuthRef = useRef<{
    songId: string | null;
    collectionType: CollectionType | null;
    collectionId: string | null;
  } | null>(null);

  if (beforeAuthRef.current === null) {
    beforeAuthRef.current = {
      songId: activeSongId,
      collectionType: activeCollectionType,
      collectionId: activeCollectionId,
    };
  }

  const restorePreviousCollection = useCallback(() => {
    const before = beforeAuthRef.current;
    const collectionType = before?.collectionType ?? null;

    if (!collectionType || collectionType === "foryou") return;

    const songId = before?.songId ?? null;
    const collectionId = before?.collectionId ?? null;
    if (!songId || !collectionId) return;

    playFromCollection({
      songId,
      collectionType,
      collectionId,
      options: { autoPlay: false },
    });
  }, [playFromCollection]);

  useEffect(() => {
    if (status !== "logged-in") return;

    closeModal();
    useNavigationStore.setState({ reload: "true" });
    setTimeout(restorePreviousCollection, 1000);
  }, [closeModal, restorePreviousCollection, status]);

  return (
    <LBox height={availableHeight} width={width}>
      {isLoading ? (
        <LBox alignItems="center" justifyContent="center" flex={1}>
          <ActivityIndicator size="large" />
        </LBox>
      ) : (
        <Auth
          backgroundColor="transparent"
          bottomSheet
          availableHeight={availableHeight}
        />
      )}
    </LBox>
  );
};

export const AuthModalSheet = forwardRef<SheetAdapterRef>(
  function AuthModalSheet(_props, ref) {
    return (
      <AppModalSheet
        ref={ref}
        showHandle={false}
        surface={<DarkSheetSurface />}
      >
        {({ closeModal }) => <AuthModalContent closeModal={closeModal} />}
      </AppModalSheet>
    );
  },
);

export default AuthModalSheet;

const styles = StyleSheet.create({
  darkSurface: {
    backgroundColor: "#17171C",
    borderTopLeftRadius: APP_MODAL_CORNER_RADIUS,
    borderTopRightRadius: APP_MODAL_CORNER_RADIUS,
    overflow: "hidden",
  },
});
